import { useId, type ReactNode } from 'react';

import { normalizeLook, type AvatarLook } from '@shared/avatar';

/**
 * Busto del avatar a partir de un `AvatarLook`: cabeza, hombros y pecho en un
 * encuadre cuadrado, el mismo para la lista de jugadores y para el perfil.
 * El cuerpo se dibuja más largo de lo que se ve y el encuadre lo corta.
 */

/** Aclara (+) u oscurece (−) un color hex en una fracción de 0 a 1. */
function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex, 16);
  const channel = (value: number) => {
    const target = amount < 0 ? 0 : 255;
    return Math.round(value + (target - value) * Math.abs(amount));
  };
  const r = channel((n >> 16) & 255);
  const g = channel((n >> 8) & 255);
  const b = channel(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

const c = (hex: string) => `#${hex}`;
const INK = '#1d1917';

/** Contorno: tinta casi negra en todas las piezas, al estilo de ilustración vectorial. */
const line = (_hex: string) => INK;

/** Grosor del contorno de las piezas grandes (cabeza, cuello, torso). */
const OUTLINE = 1.5;

/* ------------------------------------------------------------------ */
/* Geometría del cuerpo                                                */
/* ------------------------------------------------------------------ */

interface Frame {
  female: boolean;
  /** Arriba de la costura del brazo izquierdo (el derecho es simétrico respecto a x = 60). */
  armX: number;
  torso: string;
}

function frameFor(female: boolean): Frame {
  return female
    ? {
        female,
        armX: 34,
        torso: 'M51,77 L33.5,83.5 Q26,86 24.5,93 L21,124 L99,124 L95.5,93 Q94,86 86.5,83.5 L69,77 Z',
      }
    : {
        female,
        armX: 31.5,
        torso: 'M50,77 L31,83.5 Q23,86 21.5,93 L17,124 L103,124 L98.5,93 Q97,86 89,83.5 L70,77 Z',
      };
}

/*
 * Proporciones del busto: la cabeza (del pelo a la barbilla) ocupa casi seis
 * décimas del alto, el cuello es ancho y corto, y los hombros miden unas dos
 * caras de ancho y caen poco.
 *
 * La cabeza se dibuja en su propio sistema (de x = 36 a 84, barbilla en
 * y = 86) y se coloca aquí con la barbilla en y = 71.
 */
const HEAD_TRANSFORM = 'translate(60 71) scale(0.85) translate(-60 -86)';

/**
 * Los detalles de la ropa y los complementos del cuello se dibujan con el
 * escote en y = 87 y un cuello de 16 de ancho; esto los lleva al escote real.
 */
const BODY_TRANSFORM = 'translate(60 77) scale(1.25) translate(-60 -87)';
const BODY_TRANSFORM_INVERSE = 'translate(60 87) scale(0.8) translate(-60 -77)';

/* ------------------------------------------------------------------ */
/* Pelo                                                                */
/* ------------------------------------------------------------------ */

function HairBack({ look }: { look: AvatarLook }) {
  const fill = c(look.hairColor);
  const stroke = line(look.hairColor);
  const p = { fill, stroke, strokeWidth: 1.2 };
  switch (look.hair) {
    case 6: // Afro
      return <circle cx={60} cy={46} r={37} {...p} />;
    case 10: // Coleta
      return <path d="M78,34 C98,40 100,70 92,96 C90,104 84,104 84,96 C88,74 86,54 72,42 Z" {...p} />;
    case 11: // Largo liso
      return <path d="M33,50 C29,11 91,11 87,50 L90,112 Q60,120 30,112 Z" {...p} />;
    case 12: // Largo ondulado
      return (
        <path
          d="M33,48 C29,11 91,11 87,48 C94,62 86,72 92,84 C98,96 88,106 92,116 Q60,124 28,116 C32,106 22,96 28,84 C34,72 26,62 33,48 Z"
          {...p}
        />
      );
    case 13: // Media melena
      return <path d="M32,50 C29,11 91,11 88,50 L90,84 Q84,90 76,86 L44,86 Q36,90 30,84 Z" {...p} />;
    case 15: // Flequillo
      return <path d="M32,50 C29,11 91,11 88,50 L89,90 Q60,96 31,90 Z" {...p} />;
    case 16: // Dos coletas bajas
      return (
        <path
          d="M34,46 Q28,56 30,68 Q24,82 30,98 Q33,102 36,98 Q38,82 35,68 Z M86,46 Q92,56 90,68 Q96,82 90,98 Q87,102 84,98 Q82,82 85,68 Z"
          {...p}
        />
      );
    case 21: // Bob, a la altura de la barbilla
      return <path d="M32,50 C29,11 91,11 88,50 L90,80 Q84,85 78,81 L42,81 Q36,85 30,80 Z" {...p} />;
    case 22: // Rizos largos
      return (
        <g {...p}>
          {[
            [32, 48], [29, 60], [30, 72], [29, 84], [32, 96], [36, 106],
            [88, 48], [91, 60], [90, 72], [91, 84], [88, 96], [84, 106],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={8} />
          ))}
          <path d="M33,50 C29,11 91,11 87,50 L86,100 L34,100 Z" stroke="none" />
        </g>
      );
    case 23: // Melena lateral, cayendo por un lado
      return (
        <path d="M33,48 C29,11 91,11 87,48 C90,64 92,86 95,112 Q87,119 78,111 C80,92 77,72 73,58 L40,58 Q38,66 36,74 Q32,66 33,48 Z" {...p} />
      );
    case 17: // Rastas
      return (
        <g stroke={stroke} strokeWidth={7.5} strokeLinecap="round">
          {[34, 40, 46, 74, 80, 86].map((x, i) => (
            <line key={x} x1={x} y1={44} x2={x + (i < 3 ? -3 : 3)} y2={100 + (i % 2) * 8} />
          ))}
          <g stroke={fill} strokeWidth={5.5}>
            {[34, 40, 46, 74, 80, 86].map((x, i) => (
              <line key={x} x1={x} y1={44} x2={x + (i < 3 ? -3 : 3)} y2={100 + (i % 2) * 8} />
            ))}
          </g>
        </g>
      );
    default:
      return null;
  }
}

/** Casquete básico que cubre la parte de arriba de la cabeza. */
const CAP = 'M33,58 C27,11 93,11 87,58 C85,46 80,40 72,38 C62,42 50,42 42,39 C38,43 35,50 33,58 Z';

function HairFront({ look }: { look: AvatarLook }) {
  const fill = c(look.hairColor);
  const stroke = line(look.hairColor);
  const p = { fill, stroke, strokeWidth: 1.2, strokeLinejoin: 'round' as const };
  switch (look.hair) {
    case 0: // Calvo: un brillo
      return <ellipse cx={52} cy={33} rx={6} ry={3} fill="#fff" opacity={0.25} />;
    case 1: // Rapado
      return <path d="M34,54 C30,11 90,11 86,54 C82,42 74,37 60,37 C46,37 38,42 34,54 Z" fill={fill} opacity={0.55} />;
    case 2: // Corto
      return <path d={CAP} {...p} />;
    case 3: // Despeinado
      return (
        <path
          d="M33,58 C30,40 32,30 38,26 L36,18 L46,24 L50,14 L57,23 L64,12 L68,23 L78,16 L78,27 L88,26 C90,36 89,46 87,58 C84,46 78,40 70,40 L64,34 L58,40 L50,36 L44,42 C38,45 35,50 33,58 Z"
          {...p}
        />
      );
    case 4: // Raya al lado
      return <path d="M33,58 C27,11 93,11 87,58 C85,44 82,38 76,36 C66,36 52,40 44,48 C46,40 48,36 50,34 C42,38 36,46 33,58 Z" {...p} />;
    case 5: // Tupé
      return (
        <path
          d="M33,58 C30,40 34,30 40,26 C42,14 60,8 76,14 C86,18 88,26 84,30 C89,38 89,48 87,58 C85,46 80,40 72,38 C60,40 48,40 42,40 C37,44 35,50 33,58 Z"
          {...p}
        />
      );
    case 6: // Afro: el frente es parte del volumen
      return <path d="M34,50 C38,34 82,34 86,50 C80,42 70,40 60,42 C50,40 40,42 34,50 Z" {...p} />;
    case 7: // Rizos cortos
      return (
        <g {...p}>
          {[
            [36, 46, 7],
            [40, 35, 8],
            [50, 28, 8],
            [61, 25, 8.5],
            [72, 28, 8],
            [81, 35, 8],
            [85, 46, 7],
          ].map(([x, y, r]) => (
            <circle key={x} cx={x} cy={y} r={r} />
          ))}
        </g>
      );
    case 8: // Cresta
      return (
        <>
          <path d="M34,54 C30,11 90,11 86,54 C82,42 74,37 60,37 C46,37 38,42 34,54 Z" fill={fill} opacity={0.4} />
          <path d="M53,40 C52,26 54,14 60,4 C66,14 68,26 67,40 Z" {...p} />
        </>
      );
    case 9: // Moño
      return (
        <>
          <circle cx={60} cy={20} r={10} {...p} />
          <path d={CAP} {...p} />
        </>
      );
    case 11: // Largo liso: raya al medio
      return <path d="M33,62 C27,11 93,11 87,62 C84,46 72,36 60,34 C48,36 36,46 33,62 Z" {...p} />;
    case 12: // Largo ondulado
      return <path d="M33,60 C27,11 93,11 87,60 C84,44 74,36 62,36 C58,42 46,44 40,42 C36,46 34,52 33,60 Z" {...p} />;
    case 13: // Media melena con flequillo ladeado
      return <path d="M32,62 C27,11 93,11 88,62 C86,48 82,42 78,40 C66,44 50,48 38,46 C35,50 33,56 32,62 Z" {...p} />;
    case 14: // Trenzas
      return (
        <>
          <path d="M33,62 C27,11 93,11 87,62 C84,46 72,36 60,34 C48,36 36,46 33,62 Z" {...p} />
          {[-1, 1].map((side) => (
            <g key={side} {...p}>
              {[66, 76, 86, 96].map((y) => (
                <ellipse key={y} cx={60 + side * 27} cy={y} rx={5} ry={6} />
              ))}
              <rect x={60 + side * 27 - 3} y={101} width={6} height={4} rx={1} fill={c(look.accColor)} stroke="none" />
            </g>
          ))}
        </>
      );
    case 15: // Flequillo recto
      return <path d="M33,60 C27,11 93,11 87,60 L86,46 L34,46 Z" {...p} />;
    case 16: // Dos coletas bajas: el pelo cae por detrás; aquí, las gomas
      return (
        <>
          <path d={CAP} {...p} />
          <rect x={29} y={66} width={6} height={3.4} rx={1.2} fill={c(look.accColor)} />
          <rect x={85} y={66} width={6} height={3.4} rx={1.2} fill={c(look.accColor)} />
        </>
      );
    case 10: // Coleta
    case 17: // Rastas
      return <path d={CAP} {...p} />;
    case 18: // Hacia atrás
      return (
        <>
          <path d="M34,56 C29,13 91,13 86,56 C84,42 80,34 72,31 C64,29 56,29 48,31 C40,34 36,42 34,56 Z" {...p} />
          <path d="M45,24 Q60,15 75,24 M43,29.5 Q60,20 77,29.5" fill="none" stroke={shade(look.hairColor, -0.35)} strokeWidth={0.9} strokeLinecap="round" />
        </>
      );
    case 19: // Entradas
      return (
        <path d="M34,56 C29,13 91,13 86,56 C85,46 82,38 77,36 C73,30 67,31 60,34 C53,31 47,30 43,36 C38,38 35,46 34,56 Z" {...p} />
      );
    case 20: // Pixie, con el flequillo ladeado
      return <path d="M34,58 C28,12 92,12 86,58 C85,48 83,42 80,40 C70,42 56,44 43,53 C41,49 38,52 34,58 Z" {...p} />;
    case 21: // Bob con raya al lado
      return <path d="M32,64 C27,11 93,11 88,64 C86,50 82,42 74,38 C64,38 52,40 44,44 C38,48 34,54 32,64 Z" {...p} />;
    case 22: // Rizos largos
      return (
        <g {...p}>
          {[
            [36, 46, 7], [39, 35, 8], [49, 28, 8], [60, 25, 8.5], [71, 28, 8], [81, 35, 8], [84, 46, 7],
          ].map(([x, y, r]) => (
            <circle key={x} cx={x} cy={y} r={r} />
          ))}
        </g>
      );
    case 23: // Melena lateral
      return <path d="M33,62 C27,11 93,11 87,62 C86,50 82,42 78,40 C66,38 52,38 40,46 C36,50 34,56 33,62 Z" {...p} />;
    case 24: // Degradado: arriba corto y los lados casi rapados
      return (
        <>
          <path d="M34,56 C30,11 90,11 86,56 C82,42 74,37 60,37 C46,37 38,42 34,56 Z" fill={fill} opacity={0.45} />
          <path d="M38,42 C37,15 83,15 82,42 C76,37 66,36 60,36.5 C52,36 44,37 38,42 Z" {...p} />
        </>
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Cara                                                                */
/* ------------------------------------------------------------------ */

function Eyes({ look }: { look: AvatarLook }) {
  // Ojos de óvalo macizo; el color elegido tiñe el óvalo, oscurecido.
  const iris = shade(look.eyeColor, -0.45);
  const oval = (x: number, rx = 2.5, ry = 3.4, cy = 57) => (
    <g key={x}>
      <ellipse cx={x} cy={cy} rx={rx} ry={ry} fill={iris} />
      <circle cx={x + rx * 0.35} cy={cy - ry * 0.4} r={0.55} fill="#fff" opacity={0.7} />
    </g>
  );
  const stroke = { fill: 'none', stroke: INK, strokeWidth: 1.7, strokeLinecap: 'round' as const };
  switch (look.eyes) {
    case 1: // Abiertos
      return <>{[50, 70].map((x) => oval(x, 2.9, 4))}</>;
    case 2: // Sonrientes
      return (
        <>
          {[50, 70].map((x) => (
            <path key={x} d={`M${x - 3.6},58 Q${x},53.6 ${x + 3.6},58`} {...stroke} />
          ))}
        </>
      );
    case 3: // Párpado caído
      return (
        <>
          {[50, 70].map((x) => (
            <g key={x}>
              {oval(x, 2.6, 3, 57.6)}
              <path d={`M${x - 3.4},55.4 L${x + 3.4},55.4 L${x + 3.4},52 L${x - 3.4},52 Z`} fill={c(look.skin)} />
              <path d={`M${x - 3.6},55.6 Q${x},54.8 ${x + 3.6},55.6`} {...stroke} strokeWidth={1.4} />
            </g>
          ))}
        </>
      );
    case 4: // Guiño
      return (
        <>
          {oval(50)}
          <path d="M66.4,57.4 Q70,59.4 73.6,57.4" {...stroke} />
        </>
      );
    case 5: // Delineados
      return (
        <>
          {[50, 70].map((x) => oval(x))}
          <path d="M47.2,54 l-2,-1.6 M72.8,54 l2,-1.6 M48.4,53.4 l-1.2,-2 M71.6,53.4 l1.2,-2" {...stroke} strokeWidth={1.3} />
        </>
      );
    case 6: // Serios
      return <>{[50, 70].map((x) => oval(x, 2.7, 2.2))}</>;
    default:
      return (
        <>
          {[50, 70].map((x) => oval(x))}
          {look.body === 1 ? <path d="M47.4,54.2 l-1.8,-1.4 M72.6,54.2 l1.8,-1.4" {...stroke} strokeWidth={1.1} /> : null}
        </>
      );
  }
}

function Brows({ look }: { look: AvatarLook }) {
  const color = shade(look.hairColor, look.hair === 0 ? -0.35 : -0.25);
  const d: Record<number, [string, string]> = {
    0: ['M45,49.5 Q50,47 55,49', 'M65,49 Q70,47 75,49.5'],
    1: ['M44.5,49.5 Q50,46 55.5,48.5', 'M64.5,48.5 Q70,46 75.5,49.5'],
    2: ['M45.5,49 Q50,47.5 54.5,48.8', 'M65.5,48.8 Q70,47.5 74.5,49'],
    3: ['M45,50 Q49,44.5 55,48', 'M65,48 Q71,44.5 75,50'],
    4: ['M45,47 L55,50.5', 'M65,50.5 L75,47'],
    5: ['M45,50 L55,46.5', 'M65,46.5 L75,50'],
  };
  const width = look.brows === 1 ? 3.2 : look.brows === 2 ? 1.5 : 2.3;
  return (
    <g stroke={color} strokeWidth={width} strokeLinecap="round" fill="none">
      {d[look.brows]!.map((path) => (
        <path key={path} d={path} />
      ))}
    </g>
  );
}

function Nose({ look }: { look: AvatarLook }) {
  // La nariz es una mancha de sombra, sin contorno.
  const dark = shade(look.skin, -0.14);
  switch (look.nose) {
    case 1: // Pequeña
      return <ellipse cx={60} cy={66} rx={2.8} ry={1.9} fill={dark} />;
    case 2: // Grande
      return <path d="M55,66.4 Q55,61.4 60,61.4 Q65,61.4 65,66.4 Q65,69.2 60,69.2 Q55,69.2 55,66.4 Z" fill={dark} />;
    case 3: // Aguileña
      return <path d="M59.6,57 Q64.6,63 63.8,67.4 Q60,68.8 56.6,67 Q60,64 59.6,57 Z" fill={dark} />;
    default:
      return <path d="M56.2,66.2 Q56.2,62.6 60,62.6 Q63.8,62.6 63.8,66.2 Q63.8,68.4 60,68.4 Q56.2,68.4 56.2,66.2 Z" fill={dark} />;
  }
}

function Mouth({ look }: { look: AvatarLook }) {
  const stroke = { fill: 'none', stroke: INK, strokeWidth: 1.5, strokeLinecap: 'round' as const };
  // La sombra del hoyuelo de la barbilla, bajo la boca.
  const chin = <path d="M57.4,80.6 Q60,82.6 62.6,80.6 Q60,81.4 57.4,80.6 Z" fill={shade(look.skin, -0.2)} />;
  switch (look.mouth) {
    case 1: // Risa
      return (
        <>
          <path d="M52.6,70.6 Q60,73 67.4,70.6 Q66,79.6 60,79.6 Q54,79.6 52.6,70.6 Z" fill="#5a2422" stroke={INK} strokeWidth={1.3} strokeLinejoin="round" />
          <path d="M53.6,71.2 Q60,73.2 66.4,71.2 L65.8,73.6 Q60,74.8 54.2,73.6 Z" fill="#fff" />
          <path d="M56.4,78 Q60,75.6 63.6,78 Q60,79.4 56.4,78 Z" fill="#d8706e" />
          {chin}
        </>
      );
    case 2: // Neutra
      return <path d="M55,72.8 L65,72.8" {...stroke} />;
    case 3: // Sorpresa
      return <ellipse cx={60} cy={74} rx={2.6} ry={3.2} fill="#5a2422" stroke={INK} strokeWidth={1.3} />;
    case 5: // Seria
      return <path d="M55,74 Q60,71.4 65,74" {...stroke} />;
    case 6: // Labios pintados
      return (
        <>
          <path d="M54.4,72.4 Q57.4,69.6 60,71 Q62.6,69.6 65.6,72.4 Q60,77.4 54.4,72.4 Z" fill="#b8343f" stroke={INK} strokeWidth={1.1} strokeLinejoin="round" />
          <path d="M54.8,72.4 Q60,73.4 65.2,72.4" fill="none" stroke={INK} strokeWidth={0.8} />
        </>
      );
    case 7: // Media sonrisa
      return <path d="M54.6,72.6 Q60,75.4 65.6,70.6" {...stroke} />;
    default: // Sonrisa abierta
      return (
        <>
          <path d="M52.6,70.6 Q60,72.8 67.4,70.6 Q65.6,78 60,78 Q54.4,78 52.6,70.6 Z" fill="#fff" stroke={INK} strokeWidth={1.3} strokeLinejoin="round" />
          {chin}
        </>
      );
  }
}

function Marks({ look }: { look: AvatarLook }) {
  switch (look.marks) {
    case 1:
      return (
        <g fill="#d9705f" opacity={0.14}>
          <ellipse cx={44} cy={66} rx={5} ry={3} />
          <ellipse cx={76} cy={66} rx={5} ry={3} />
        </g>
      );
    case 2:
      return (
        <g fill={shade(look.skin, -0.35)} opacity={0.7}>
          {[
            [44, 63],
            [47, 65.5],
            [42, 66],
            [45.5, 68],
            [76, 63],
            [73, 65.5],
            [78, 66],
            [74.5, 68],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={0.75} />
          ))}
        </g>
      );
    case 3:
      return <circle cx={71} cy={70} r={1} fill="#3a2418" />;
    case 4:
      return (
        <path d="M72,45 L68,60 M69,49 l3,1 M68.6,53 l3,1 M67.6,57 l3,1" stroke={shade(look.skin, -0.3)} strokeWidth={1.1} strokeLinecap="round" />
      );
    case 5: // Maquillaje
      return (
        <>
          <g fill={c(look.accColor)} opacity={0.4}>
            <path d="M45.6,56.6 Q49.4,51 54.4,55.8 Q49.6,53.6 45.6,56.6 Z" />
            <path d="M65.6,55.8 Q70.6,51 74.4,56.4 Q70.4,53.6 65.6,55.8 Z" />
          </g>
          <g fill="#d9705f" opacity={0.14}>
            <ellipse cx={44} cy={66} rx={5} ry={3} />
            <ellipse cx={76} cy={66} rx={5} ry={3} />
          </g>
        </>
      );
    default:
      return null;
  }
}

function Beard({ look }: { look: AvatarLook }) {
  const fill = c(look.beardColor);
  const stroke = line(look.beardColor);
  const p = { fill, stroke, strokeWidth: 1, strokeLinejoin: 'round' as const };
  const mustache = <path d="M52,70 Q56,66 60,68 Q64,66 68,70 Q64,69.5 60,70 Q56,69.5 52,70 Z" {...p} />;
  switch (look.beard) {
    case 1:
      return <path d="M36,62 Q38,82 60,83 Q82,82 84,62 Q80,74 70,76 Q60,68 50,76 Q40,74 36,62 Z" fill={fill} opacity={0.28} />;
    case 2:
      return mustache;
    case 3:
      return (
        <>
          {mustache}
          <path d="M55,77 Q60,75.5 65,77 Q64,85 60,86 Q56,85 55,77 Z" {...p} />
        </>
      );
    case 4:
      return (
        <path
          d="M35,58 Q34,86 60,88 Q86,86 85,58 Q82,70 76,74 Q70,67 60,67 Q50,67 44,74 Q38,70 35,58 Z M54,72 Q60,76 66,72 Q60,70 54,72 Z"
          fillRule="evenodd"
          {...p}
        />
      );
    case 5:
      return (
        <path
          d="M35,58 Q33,84 46,94 Q54,108 60,110 Q66,108 74,94 Q87,84 85,58 Q82,70 76,74 Q70,67 60,67 Q50,67 44,74 Q38,70 35,58 Z M54,72 Q60,76 66,72 Q60,70 54,72 Z"
          fillRule="evenodd"
          {...p}
        />
      );
    case 7: // Barba corta
      return (
        <path
          d="M37,62 Q37,84 60,86.5 Q83,84 83,62 Q80,72 75,75 Q70,68 60,68 Q50,68 45,75 Q40,72 37,62 Z M54,72 Q60,76 66,72 Q60,70 54,72 Z"
          fillRule="evenodd"
          fill={fill}
          opacity={0.8}
        />
      );
    case 8: // Candado: bigote y perilla unidos alrededor de la boca
      return (
        <path
          d="M52,70 Q56,66 60,68 Q64,66 68,70 Q69,80 60,85 Q51,80 52,70 Z M54.6,72 Q60,73 65.4,72 Q65,79 60,80.6 Q55,79 54.6,72 Z"
          fillRule="evenodd"
          {...p}
        />
      );
    case 9: // Patillas
      return <path d="M36.6,46 L41,46 L41.4,66 Q39.4,67.6 37.2,64.6 Z M83.4,46 L79,46 L78.6,66 Q80.6,67.6 82.8,64.6 Z" {...p} />;
    case 6: // Bigote imperial
      return (
        <path
          d="M60,68.5 Q55,66 51,69 Q47,72 44,67 Q44,73 49,73 Q55,73 60,70 Q65,73 71,73 Q76,73 76,67 Q73,72 69,69 Q65,66 60,68.5 Z"
          {...p}
        />
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Ropa                                                                */
/* ------------------------------------------------------------------ */

/** Silueta de caballo para la camiseta de la casa. */
const KNIGHT =
  'M-6,9 L6,9 L5,5 Q4,1 6,-3 Q7,-9 1,-10 L-1,-12.5 L-2.2,-9.6 Q-4,-9 -5,-6 L-8,-1.5 Q-8.4,1 -6,1.4 L-3,0 Q-2,2 -3,4 Q-5,6 -6,9 Z';

function Top({ look, frame, uid }: { look: AvatarLook; frame: Frame; uid: string }) {
  const main = look.topColor;
  const second = look.topColor2;
  const skin = look.skin;
  const style = look.top;
  const p = { fill: c(main), stroke: INK, strokeWidth: OUTLINE, strokeLinejoin: 'round' as const };

  // La costura del brazo: una línea de tinta y una franja en sombra hacia fuera.
  const ax = frame.armX;
  const armShade = shade(style === 1 ? skin : main, -0.14);
  const seams = ([1, -1] as const).map((side) => {
    const x = (v: number) => (side === 1 ? v : 120 - v);
    return (
      <g key={side}>
        <path d={`M${x(ax)},85 L${x(ax + 3.5)},124 L${x(ax - 1)},124 Q${x(ax - 4)},104 ${x(ax - 1)},87 Z`} fill={armShade} />
        <path d={`M${x(ax)},85 L${x(ax + 3.5)},124`} stroke={INK} strokeWidth={1.2} strokeLinecap="round" />
      </g>
    );
  });

  const clip = `${uid}-torso`;
  // El recorte del torso, en el sistema de los detalles.
  const clipDef = (
    <clipPath id={clip}>
      <path d={frame.torso} transform={BODY_TRANSFORM_INVERSE} />
    </clipPath>
  );
  const torsoOutline = <path d={frame.torso} transform={BODY_TRANSFORM_INVERSE} fill="none" stroke={INK} strokeWidth={OUTLINE} />;

  const crew = <path d="M52,87 Q60,94 68,87" fill={c(skin)} stroke={line(main)} strokeWidth={1.2} />;

  let details: ReactNode = null;

  switch (style) {
    case 0: // Camiseta
      details = crew;
      break;
    case 1: // Tirantes
      details = (
        <path
          d={`M${frame.female ? 41 : 39},124 L${frame.female ? 42 : 40},104 Q44,96 45,86 L50,86 Q60,106 70,86 L75,86 Q76,96 ${frame.female ? 78 : 80},104 L${frame.female ? 79 : 81},124 Z`}
          clipPath={`url(#${clip})`}
          {...p}
        />
      );
      break;
    case 2: // Camisa
      details = (
        <>
          <path d="M52,87 L60,97 L68,87" fill={c(skin)} />
          <path d="M51,86 L56,97 L60,92 Z M69,86 L64,97 L60,92 Z" fill={c(second)} stroke={line(second)} strokeWidth={0.9} strokeLinejoin="round" />
          <line x1={60} y1={96} x2={60} y2={141} stroke={line(main)} strokeWidth={0.8} />
          {[104, 114, 124, 134].map((y) => (
            <circle key={y} cx={60} cy={y} r={1} fill={c(second)} stroke={line(main)} strokeWidth={0.4} />
          ))}
          <rect x={65} y={100} width={9} height={7} rx={1} fill="none" stroke={line(main)} strokeWidth={0.8} />
        </>
      );
      break;
    case 3: // Sudadera
      details = (
        <>
          {/* Capucha bajada, enrollada alrededor del cuello. */}
          <path d="M41,97 Q42,86 52,85 L68,85 Q78,86 79,97 Q70,92 60,100 Q50,92 41,97 Z" fill={shade(main, -0.12)} stroke={line(main)} strokeWidth={1} />
          <path d="M56,98 L55.4,112 M64,98 L64.6,112" stroke={c(second)} strokeWidth={1.1} strokeLinecap="round" />
        </>
      );
      break;
    case 4: // Jersey
      details = (
        <>
          <path d="M50,87 Q60,96 70,87" fill={c(skin)} stroke={shade(main, -0.2)} strokeWidth={2.6} />
        </>
      );
      break;
    case 5: // Chaqueta abierta sobre camiseta
      details = (
        <>
          <path d="M50,87 L70,87 L68,142 L52,142 Z" fill={c(second)} stroke={line(second)} strokeWidth={0.9} />
          <path d="M53,87 Q60,93 67,87" fill={c(skin)} />
          <path d="M49,87 L55,104 L52,142 M71,87 L65,104 L68,142" fill="none" stroke={line(main)} strokeWidth={1.2} />
        </>
      );
      break;
    case 6: // Traje
      details = (
        <>
          <path d="M51,87 L69,87 L60,116 Z" fill="#fbfbfb" stroke="#bbb" strokeWidth={0.8} />
          <path d="M53,87 Q60,92 67,87" fill={c(skin)} />
          <path d="M50,87 L57,108 L60,116 L52,100 Z M70,87 L63,108 L60,116 L68,100 Z" fill={shade(main, -0.18)} stroke={line(main)} strokeWidth={1} strokeLinejoin="round" />
          {[122, 131].map((y) => (
            <circle key={y} cx={61} cy={y} r={1.3} fill={line(main)} />
          ))}
          <path d="M70,108 l7,0" stroke={c(second)} strokeWidth={2} strokeLinecap="round" />
        </>
      );
      break;
    case 7: // Vestido
      details = (
        <>
          <path d="M48,87 Q54,100 60,96 Q66,100 72,87" fill={c(skin)} stroke={c(second)} strokeWidth={2} />
          <path d="M42,108 Q60,112 78,108" fill="none" stroke={c(second)} strokeWidth={2.4} />
        </>
      );
      break;
    case 8: // Rayas
      details = (
        <>
          <g clipPath={`url(#${clip})`} fill={c(second)}>
            {[93, 101, 109, 117].map((y) => (
              <rect key={y} x={0} y={y} width={120} height={3.5} />
            ))}
          </g>
          {torsoOutline}
          {crew}
        </>
      );
      break;
    case 9: // Camiseta con el caballo
      details = (
        <>
          {crew}
          <g transform="translate(60 106) scale(1.2)">
            <path d={KNIGHT} fill={c(second)} stroke={line(main)} strokeWidth={0.5} strokeLinejoin="round" />
          </g>
        </>
      );
      break;
    case 10: // Polo
      details = (
        <>
          <path d="M53,87 L60,98 L67,87" fill={c(skin)} />
          <path d="M50,86 L56,96 L60,90 Z M70,86 L64,96 L60,90 Z" fill={c(main)} stroke={line(main)} strokeWidth={1} strokeLinejoin="round" />
          <line x1={60} y1={96} x2={60} y2={106} stroke={line(main)} strokeWidth={0.9} />
          {[99, 104].map((y) => (
            <circle key={y} cx={61.6} cy={y} r={0.9} fill={c(second)} />
          ))}
          <path d="M67,100 l5,0" stroke={c(second)} strokeWidth={1.6} strokeLinecap="round" />
        </>
      );
      break;
    case 11: // Jersey de rombos (argyle)
      details = (
        <>
          <g clipPath={`url(#${clip})`}>
            {[0, 1, 2].map((row) =>
              [12, 24, 36, 48, 60, 72, 84, 96, 108].map((x, col) => {
                const y = 100 + row * 16;
                const on = (row + col) % 2 === 0;
                return (
                  <path
                    key={`${row}-${col}`}
                    d={`M${x},${y - 8} L${x + 6},${y} L${x},${y + 8} L${x - 6},${y} Z`}
                    fill={on ? c(second) : shade(main, -0.15)}
                    opacity={on ? 0.9 : 1}
                  />
                );
              }),
            )}
          </g>
          {torsoOutline}
          <path d="M50,87 Q60,96 70,87" fill={c(skin)} stroke={shade(main, -0.25)} strokeWidth={2.4} />
        </>
      );
      break;
    case 12: // Cuello alto
      details = (
        <>
          <path d="M50.5,91 L50.5,80 Q60,82.5 69.5,80 L69.5,91 Q60,94 50.5,91 Z" {...p} strokeWidth={1.1} />
          <path d="M51,84.5 Q60,87 69,84.5 M51,88 Q60,90.5 69,88" fill="none" stroke={shade(main, -0.25)} strokeWidth={0.8} />
        </>
      );
      break;
    case 13: // Jersey de pico, con la camisa asomando
      details = (
        <>
          <path d="M51,87 L60,103 L69,87 Z" fill={c(second)} />
          <path d="M52,86.6 L56.4,93.6 L60,89.4 Z M68,86.6 L63.6,93.6 L60,89.4 Z" fill={c(second)} stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
          <path d="M50,87 L60,104 L70,87" fill="none" stroke={shade(main, -0.22)} strokeWidth={2.2} strokeLinejoin="round" />
          <path d="M49.4,86.6 L60,105.4 L70.6,86.6" fill="none" stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
        </>
      );
      break;
    case 14: // Camisa de cuadros
      details = (
        <>
          <g clipPath={`url(#${clip})`} opacity={0.55}>
            {[28, 36, 44, 52, 60, 68, 76, 84, 92].map((x) => (
              <rect key={`v${x}`} x={x} y={80} width={3} height={50} fill={c(second)} />
            ))}
            {[92, 100, 108, 116, 124].map((y) => (
              <rect key={`h${y}`} x={20} y={y} width={80} height={3} fill={c(second)} />
            ))}
          </g>
          <g clipPath={`url(#${clip})`}>
            {[29.5, 45.5, 61.5, 77.5].map((x) => (
              <rect key={x} x={x} y={80} width={0.8} height={50} fill={shade(main, -0.35)} />
            ))}
          </g>
          {torsoOutline}
          <path d="M52,87 L60,97 L68,87" fill={c(skin)} />
          <path d="M51,86 L56,97 L60,92 Z M69,86 L64,97 L60,92 Z" fill={c(main)} stroke={INK} strokeWidth={0.9} strokeLinejoin="round" />
          <line x1={60} y1={96} x2={60} y2={126} stroke={INK} strokeWidth={0.8} />
          {[104, 114, 124].map((y) => (
            <circle key={y} cx={61.4} cy={y} r={0.9} fill="#f4f1ea" stroke={INK} strokeWidth={0.4} />
          ))}
        </>
      );
      break;
    case 15: // Americana con camisa blanca y corbata
      details = (
        <>
          <path d="M51,87 L69,87 L64,124 L56,124 Z" fill="#f7f5f0" />
          <path d="M52,86.6 L56.4,92.6 L60,89 Z M68,86.6 L63.6,92.6 L60,89 Z" fill="#f7f5f0" stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
          <path d="M58.2,89 L61.8,89 L61.2,92 L63,112 L60,116 L57,112 L58.8,92 Z" fill={c(second)} stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
          <path d="M50,86 L57,108 L56,124 L49,124 Z M70,86 L63,108 L64,124 L71,124 Z" fill={c(main)} stroke={INK} strokeWidth={1} strokeLinejoin="round" />
          <path d="M50,86 L46,95 L53,99 L57,108 Z M70,86 L74,95 L67,99 L63,108 Z" fill={shade(main, -0.15)} stroke={INK} strokeWidth={1} strokeLinejoin="round" />
          <path d="M71,104 l7,-0.4" stroke={INK} strokeWidth={0.8} strokeLinecap="round" />
        </>
      );
      break;
    case 16: // Chaleco sobre camisa
      details = (
        <>
          <path d="M51,87 L60,108 L69,87 Z" fill="#f7f5f0" />
          <path d="M52,86.6 L56.4,92.6 L60,89 Z M68,86.6 L63.6,92.6 L60,89 Z" fill="#f7f5f0" stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
          <path d="M58.4,89 L61.6,89 L61,92 L62.4,104 L60,107 L57.6,104 L59,92 Z" fill={c(second)} stroke={INK} strokeWidth={0.7} strokeLinejoin="round" />
          <path d="M50,87 L60,109 L70,87" fill="none" stroke={INK} strokeWidth={1.1} strokeLinejoin="round" />
          <line x1={60} y1={109} x2={60} y2={126} stroke={INK} strokeWidth={0.8} />
          {[113, 119].map((y) => (
            <circle key={y} cx={61.6} cy={y} r={0.9} fill={shade(main, -0.4)} />
          ))}
          <path d="M44,106 l7,0 M69,106 l7,0" stroke={INK} strokeWidth={0.9} strokeLinecap="round" />
        </>
      );
      break;
    case 17: // Blusa de cuello redondo
      details = (
        <>
          <path d="M52,87 Q60,95 68,87" fill={c(skin)} />
          <path d="M51,86.4 Q50.4,94 59.6,94.2 L59.6,91.6 Q54,91 52.6,86.4 Z M69,86.4 Q69.6,94 60.4,94.2 L60.4,91.6 Q66,91 67.4,86.4 Z" fill={c(second)} stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
          {[100, 106, 112].map((y) => (
            <circle key={y} cx={60} cy={y} r={0.8} fill={c(second)} stroke={INK} strokeWidth={0.35} />
          ))}
        </>
      );
      break;
    case 18: // Cazadora vaquera sobre camiseta
      details = (
        <>
          <path d="M51,87 L69,87 L67,124 L53,124 Z" fill={c(second)} />
          <path d="M53,87 Q60,93 67,87" fill={c(skin)} />
          <path d="M51,87 L53,124 M69,87 L67,124" stroke={INK} strokeWidth={1} />
          <path d="M50,85 L45,93 L53,99 L51.6,88 Z M70,85 L75,93 L67,99 L68.4,88 Z" fill={shade(main, -0.12)} stroke={INK} strokeWidth={0.9} strokeLinejoin="round" />
          <path d="M37,102 L48,102 L48,106 L42.5,108 L37,106 Z M72,102 L83,102 L83,106 L77.5,108 L72,106 Z" fill={c(main)} stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
          <g fill="none" stroke={shade(main, 0.45)} strokeWidth={0.6} strokeDasharray="1.2 1">
            <path d="M37.8,103.6 L47.2,103.6 M72.8,103.6 L82.2,103.6 M34,99 Q45,97 50,98 M86,99 Q75,97 70,98" />
          </g>
          {[42.5, 77.5].map((x) => (
            <circle key={x} cx={x} cy={106} r={0.8} fill="#b87333" />
          ))}
        </>
      );
      break;
    case 19: // Abrigo de solapas anchas
      details = (
        <>
          <path d="M52,87 L60,104 L68,87 Z" fill={c(second)} />
          <path d="M53,87 Q60,92 67,87" fill={c(skin)} />
          <path d="M51,86 L40,95 L47,102 L42,108 L58,118 L60,104 Z M69,86 L80,95 L73,102 L78,108 L62,118 L60,104 Z" fill={shade(main, -0.14)} stroke={INK} strokeWidth={1} strokeLinejoin="round" />
          {[54, 66].map((x) =>
            [112, 120].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y + 4} r={1.2} fill={shade(main, -0.45)} />),
          )}
        </>
      );
      break;
    case 20: // Escote barco
      details = (
        <>
          <path d="M30,78 L90,78 L90,89 Q60,97 30,89 Z" fill={c(skin)} clipPath={`url(#${clip})`} />
          <path d="M34,90.4 Q60,98 86,90.4" fill="none" stroke={INK} strokeWidth={0.9} clipPath={`url(#${clip})`} />
          {torsoOutline}
        </>
      );
      break;
    case 21: // Top cruzado
      details = (
        <>
          <path d="M51,87 L60,106 L69,87 Z" fill={c(skin)} />
          <path d="M50,87 L60,107 L70,87" fill="none" stroke={INK} strokeWidth={0.9} strokeLinejoin="round" />
          <path d="M60,107 Q68,114 78,124" fill="none" stroke={INK} strokeWidth={0.9} />
          <path d="M70,113 L78,110 L77,116 Z M70,113 L74,121 L68,120 Z" fill={c(second)} stroke={INK} strokeWidth={0.7} strokeLinejoin="round" />
        </>
      );
      break;
  }

  return (
    <g>
      <path d={frame.torso} fill={c(style === 1 ? skin : main)} stroke={INK} strokeWidth={OUTLINE} strokeLinejoin="round" />
      {seams}
      <g transform={BODY_TRANSFORM}>
        {clipDef}
        {details}
      </g>
    </g>
  );
}

/* ------------------------------------------------------------------ */
/* Complementos                                                        */
/* ------------------------------------------------------------------ */

function Neck({ look }: { look: AvatarLook }) {
  const acc = look.accColor;
  const p = { fill: c(acc), stroke: line(acc), strokeWidth: 1, strokeLinejoin: 'round' as const };
  switch (look.neck) {
    case 6: // Perlas
      return (
        <>
          {[-4, -3, -2, -1, 0, 1, 2, 3, 4].map((i) => {
            const t = (i + 4) / 8;
            const x = 51 + 18 * t;
            const y = 88 + 9 * 4 * t * (1 - t) * 0.9;
            return <circle key={i} cx={x} cy={y} r={1.25} fill="#f6f1e6" stroke={INK} strokeWidth={0.4} />;
          })}
        </>
      );
    case 7: // Pañuelo al cuello
      return (
        <>
          <path d="M50,85.6 Q60,91.6 70,85.6 L71,89.6 Q60,96 49,89.6 Z" {...p} />
          <path d="M56.6,91.6 L63.4,91.6 L60,102 Z" {...p} />
        </>
      );
    case 8: // Cadena
      return (
        <>
          <path d="M51,87.6 Q60,99 69,87.6" fill="none" stroke={c(acc)} strokeWidth={0.9} strokeDasharray="1.1 0.5" />
          <rect x={58.6} y={96} width={2.8} height={3.6} rx={0.6} {...p} strokeWidth={0.5} />
        </>
      );
    case 1: // Collar
      return (
        <>
          <path d="M51,88 Q60,99 69,88" fill="none" stroke={c(acc)} strokeWidth={1.2} />
          <circle cx={60} cy={96} r={2.4} {...p} />
        </>
      );
    case 2: // Bufanda
      return (
        <>
          <path d="M48,84 Q60,92 72,84 L73,91 Q60,99 47,91 Z" {...p} />
          <path d="M63,92 L68,92 L70,116 L62,116 Z" {...p} />
          <path d="M62.6,112 L69.6,112" stroke={line(acc)} strokeWidth={1} />
        </>
      );
    case 3: // Pajarita
      return <path d="M60,90 L52,86 L52,94 Z M60,90 L68,86 L68,94 Z M58,88 h4 v4 h-4 Z" {...p} />;
    case 4: // Corbata
      return <path d="M57.5,88 L62.5,88 L61.6,92 L64,114 L60,119 L56,114 L58.4,92 Z" {...p} />;
    case 5: // Medalla
      return (
        <>
          <path d="M50,87 L57,97 M70,87 L63,97" stroke={c(acc)} strokeWidth={2.4} />
          <circle cx={60} cy={100} r={5} fill="#e2b33c" stroke="#9c7314" strokeWidth={1} />
          <path d="M60,97 L61,99.3 L63.3,99.4 L61.5,100.9 L62.1,103.2 L60,101.9 L57.9,103.2 L58.5,100.9 L56.7,99.4 L59,99.3 Z" fill="#fff4c4" />
        </>
      );
    default:
      return null;
  }
}

function Earrings({ look }: { look: AvatarLook }) {
  const acc = c(look.accColor);
  const ears = [37, 83];
  switch (look.earrings) {
    case 1:
      return <>{ears.map((x) => <circle key={x} cx={x} cy={63} r={1.6} fill={acc} stroke={line(look.accColor)} strokeWidth={0.5} />)}</>;
    case 2:
      return <>{ears.map((x) => <circle key={x} cx={x} cy={65.5} r={3} fill="none" stroke={acc} strokeWidth={1.3} />)}</>;
    case 4: // Aros grandes
      return <>{ears.map((x) => <circle key={x} cx={x} cy={68} r={4.8} fill="none" stroke={acc} strokeWidth={1.2} />)}</>;
    case 3:
      return (
        <>
          {ears.map((x) => (
            <g key={x}>
              <line x1={x} y1={62.5} x2={x} y2={67} stroke={acc} strokeWidth={0.9} />
              <path d={`M${x},66 Q${x + 2.4},69.5 ${x},71.5 Q${x - 2.4},69.5 ${x},66 Z`} fill={acc} stroke={line(look.accColor)} strokeWidth={0.5} />
            </g>
          ))}
        </>
      );
    default:
      return null;
  }
}

function Glasses({ look }: { look: AvatarLook }) {
  const frame = c(look.glassesColor);
  const s = { stroke: frame, strokeWidth: 1.2, strokeLinejoin: 'round' as const };
  const bridge = <path d="M55.5,57 Q60,55 64.5,57" fill="none" {...s} />;
  const temples = <path d="M43,56 L36,55 M77,56 L84,55" {...s} />;
  switch (look.glasses) {
    case 1:
      return (
        <>
          <circle cx={50} cy={57.5} r={6} fill="#fff" fillOpacity={0.1} {...s} />
          <circle cx={70} cy={57.5} r={6} fill="#fff" fillOpacity={0.1} {...s} />
          {bridge}
          {temples}
        </>
      );
    case 2:
      return (
        <>
          <rect x={42.5} y={52.5} width={13} height={10} rx={1.5} fill="#fff" fillOpacity={0.1} {...s} />
          <rect x={64.5} y={52.5} width={13} height={10} rx={1.5} fill="#fff" fillOpacity={0.1} {...s} />
          <path d="M55.5,56 L64.5,56" {...s} />
          {temples}
        </>
      );
    case 3: // De sol
      return (
        <>
          <path d="M42,53 L56,53 L55,59 Q54,63 49,63 Q43,63 42.5,58 Z M64,53 L78,53 L77.5,58 Q77,63 71,63 Q66,63 65,59 Z" fill="#1d1d22" {...s} />
          <path d="M56,54.5 L64,54.5" {...s} />
          <path d="M45,55 l3,0 M67,55 l3,0" stroke="#fff" strokeOpacity={0.5} strokeWidth={1} strokeLinecap="round" />
          {temples}
        </>
      );
    case 4: // Aviador
      return (
        <>
          <path d="M42,53 L56,53 Q57,64 50,64 Q42,64 42,53 Z M64,53 L78,53 Q78,64 70,64 Q63,64 64,53 Z" fill="#6a5a3c" fillOpacity={0.55} {...s} strokeWidth={1.1} />
          <path d="M56,53 L64,53 M57,55.4 L63,55.4" {...s} strokeWidth={1.1} />
          {temples}
        </>
      );
    case 7: // Media montura: barra gruesa arriba, aro fino abajo
      return (
        <>
          <rect x={42.5} y={52.5} width={13} height={9.5} rx={2} fill="#fff" fillOpacity={0.1} stroke={frame} strokeWidth={0.6} />
          <rect x={64.5} y={52.5} width={13} height={9.5} rx={2} fill="#fff" fillOpacity={0.1} stroke={frame} strokeWidth={0.6} />
          <path d="M42.5,53.2 L55.5,53.2 M64.5,53.2 L77.5,53.2 M55.5,54 Q60,52.6 64.5,54" fill="none" stroke={frame} strokeWidth={2.2} strokeLinecap="round" />
          {temples}
        </>
      );
    case 8: // Ojo de gato
      return (
        <>
          <path
            d="M42.6,53.6 Q49,52 56,53.6 Q55.6,62 49.4,62 Q43.4,61.6 42.8,57 L40.6,51.4 Z M77.4,53.6 Q71,52 64,53.6 Q64.4,62 70.6,62 Q76.6,61.6 77.2,57 L79.4,51.4 Z"
            fill="#fff"
            fillOpacity={0.1}
            {...s}
            strokeWidth={1.6}
          />
          <path d="M56,55 Q60,53.6 64,55" fill="none" {...s} />
          {temples}
        </>
      );
    case 5: // Monóculo
      return (
        <>
          <circle cx={70} cy={57.5} r={6.2} fill="#fff" fillOpacity={0.1} {...s} />
          <path d="M75,62 Q80,76 72,88" fill="none" stroke={frame} strokeWidth={0.8} />
        </>
      );
    default:
      return null;
  }
}

function Hat({ look }: { look: AvatarLook }) {
  const main = look.hatColor;
  const dark = shade(main, -0.2);
  const p = { fill: c(main), stroke: line(main), strokeWidth: 1.2, strokeLinejoin: 'round' as const };
  switch (look.hat) {
    case 1: // Gorra
      return (
        <>
          <path d="M33,48 C33,12 87,12 87,48 Z" {...p} />
          <path d="M31,47 Q60,40 89,47 Q88,54 60,52 Q32,54 31,47 Z" fill={dark} stroke={line(main)} strokeWidth={1.2} />
          <circle cx={60} cy={22} r={2} fill={dark} />
          <path d="M60,23 L60,46 M46,26 Q44,36 44,47 M74,26 Q76,36 76,47" stroke={dark} strokeWidth={0.8} fill="none" />
        </>
      );
    case 2: // Gorra al revés
      return (
        <>
          <path d="M33,48 C33,12 87,12 87,48 Z" {...p} />
          <path d="M84,30 Q96,26 98,36 Q92,38 86,40 Z" fill={dark} stroke={line(main)} strokeWidth={1} />
          <rect x={55} y={40} width={10} height={6} rx={2} fill="none" stroke={dark} strokeWidth={1.2} />
          <path d="M33,46 Q60,42 87,46" fill="none" stroke={dark} strokeWidth={1.6} />
        </>
      );
    case 3: // Gorro de lana
      return (
        <>
          <circle cx={60} cy={15} r={6} fill={c(look.topColor2)} stroke={line(look.topColor2)} strokeWidth={1} />
          <path d="M34,44 C31,10 89,10 86,44 Z" {...p} />
          <rect x={32} y={39} width={56} height={10} rx={4} fill={dark} stroke={line(main)} strokeWidth={1.2} />
          <path d="M40,40 v8 M47,40 v8 M54,40 v8 M61,40 v8 M68,40 v8 M75,40 v8 M82,40 v8" stroke={shade(main, -0.35)} strokeWidth={0.8} />
        </>
      );
    case 4: // Chistera
      return (
        <>
          <path d="M42,34 L44,8 Q60,5 76,8 L78,34 Z" {...p} />
          <rect x={42} y={24} width={36} height={6} fill={c(look.accColor)} />
          <ellipse cx={60} cy={35} rx={30} ry={4.5} {...p} />
        </>
      );
    case 6: // Vaquero
      return (
        <>
          <path d="M42,36 C40,12 48,12 54,16 Q60,20 66,16 C72,12 80,12 78,36 Z" {...p} />
          <rect x={42} y={30} width={36} height={5} fill={dark} />
          <path d="M18,32 Q30,44 60,42 Q90,44 102,32 Q98,40 90,42 Q60,50 30,42 Q22,40 18,32 Z" {...p} />
        </>
      );
    case 7: // Boina
      return (
        <>
          <path d="M34,40 Q30,22 58,20 Q90,20 90,34 Q88,42 60,42 Q40,43 34,40 Z" {...p} />
          <path d="M58,20 l1,-5" stroke={line(main)} strokeWidth={2} strokeLinecap="round" />
          <path d="M36,38 Q60,43 88,36" fill="none" stroke={dark} strokeWidth={1.4} />
        </>
      );
    case 8: // Diadema
      return <path d="M35,52 C32,12 88,12 85,52" fill="none" stroke={c(main)} strokeWidth={3.6} strokeLinecap="round" />;
    case 9: // Lazo
      return (
        <g transform="translate(75 29) rotate(20)">
          <path d="M0,0 L-11,-7 Q-14,0 -11,7 Z M0,0 L11,-7 Q14,0 11,7 Z" {...p} />
          <circle r={3.2} {...p} fill={dark} />
        </g>
      );
    case 10: // Auriculares
      return (
        <>
          <path d="M33,56 C30,14 90,14 87,56" fill="none" stroke={line(main)} strokeWidth={5.2} strokeLinecap="round" />
          <path d="M33,56 C30,14 90,14 87,56" fill="none" stroke={c(main)} strokeWidth={3.4} strokeLinecap="round" />
          <rect x={27} y={48} width={9} height={17} rx={4} {...p} />
          <rect x={84} y={48} width={9} height={17} rx={4} {...p} />
        </>
      );
    case 14: // Gorro de pescador
      return (
        <>
          <path d="M37,41 C37,14 83,14 83,41 Z" {...p} />
          <path d="M37.4,36 Q60,32 82.6,36" fill="none" stroke={dark} strokeWidth={2.4} />
          <path d="M29,46 Q33,38 38,39 L82,39 Q87,38 91,46 Q60,49.6 29,46 Z" {...p} />
        </>
      );
    case 15: // Fedora
      return (
        <>
          <path d="M40,36 L43,16 Q52,12 60,17 Q68,12 77,16 L80,36 Z" {...p} />
          <path d="M41,30 L79,30 L79.6,35 L40.4,35 Z" fill={c(look.topColor2)} stroke={INK} strokeWidth={0.8} />
          <path d="M23,38 Q40,32 60,33 Q80,32 97,38 Q92,42.4 60,40.6 Q28,42.4 23,38 Z" {...p} />
        </>
      );
    case 13: // Pañuelo
      return (
        <>
          <path d="M33,50 C29,10 91,10 87,50 Q60,40 33,50 Z" {...p} />
          <g fill={shade(main, 0.45)}>
            {[
              [46, 34],
              [60, 28],
              [74, 34],
              [53, 42],
              [67, 42],
            ].map(([x, y]) => (
              <circle key={`${x}-${y}`} cx={x} cy={y} r={1.4} />
            ))}
          </g>
          <path d="M86,42 L96,40 L93,50 Z M86,44 L97,52 L90,54 Z" {...p} />
        </>
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Fondo y composición                                                 */
/* ------------------------------------------------------------------ */

const VIEWBOX = '0 2 120 120';

/** Encuadre cerrado en la cara, para las miniaturas del editor. */
export const FACE_VIEWBOX = '29 12 62 62';

/** Peinados con casquete liso, donde cabe el mechón de brillo. */
const HIGHLIGHTED_HAIR = new Set([2, 4, 5, 9, 10, 11, 12, 13, 14, 15, 16, 17, 19, 20, 21, 23]);

/** Cara ovalada con mandíbula y barbilla, más larga que ancha. */
const HEAD =
  'M36,52 C36,32 46,25 60,25 C74,25 84,32 84,52 C84,64 81,73 75,79 C70,84 65,86 60,86 C55,86 50,84 45,79 C39,73 36,64 36,52 Z';

/** La misma cara con la mandíbula más fina y la barbilla más pequeña. */
const HEAD_FEMALE =
  'M37,52 C37,32 46,25 60,25 C74,25 83,32 83,52 C83,63 80,71 74.5,77.5 C70,82.6 65,85.4 60,85.4 C55,85.4 50,82.6 45.5,77.5 C40,71 37,63 37,52 Z';

export function AvatarFigure({
  look: rawLook,
  className,
  title,
  viewBox = VIEWBOX,
}: {
  look: AvatarLook;
  className?: string;
  title?: string;
  viewBox?: string;
}) {
  const uid = useId().replace(/:/g, '');
  const look = normalizeLook(rawLook);
  const female = look.body === 1;
  const frame = frameFor(female);
  const head = female ? HEAD_FEMALE : HEAD;

  const gradient = `${uid}-bg`;
  const board = `${uid}-board`;
  const bgFill = look.bg === 3 ? `url(#${gradient})` : look.bg === 4 ? `url(#${board})` : c(look.bgColor);
  // Círculo y cuadrado recortan el busto; sin fondo, lo corta el propio encuadre.
  const shape =
    look.bg === 1 || look.bg === 4 ? (
      <circle cx={60} cy={62} r={60} />
    ) : look.bg === 0 ? null : (
      <rect x={0} y={2} width={120} height={120} rx={18} />
    );
  const clip = `${uid}-clip`;

  return (
    <svg
      viewBox={viewBox}
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {look.bg === 3 ? (
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={shade(look.bgColor, 0.35)} />
            <stop offset="1" stopColor={shade(look.bgColor, -0.25)} />
          </linearGradient>
        ) : null}
        {look.bg === 4 ? (
          <pattern id={board} width={30} height={30} patternUnits="userSpaceOnUse" x={0} y={2}>
            <rect width={30} height={30} fill={c(look.bgColor)} />
            <rect width={15} height={15} fill={shade(look.bgColor, -0.1)} />
            <rect x={15} y={15} width={15} height={15} fill={shade(look.bgColor, -0.1)} />
          </pattern>
        ) : null}
        {shape ? <clipPath id={clip}>{shape}</clipPath> : null}
      </defs>
      <g clipPath={shape ? `url(#${clip})` : undefined}>
        {shape ? <rect x={0} y={2} width={120} height={120} fill={bgFill} /> : null}
        <g transform={HEAD_TRANSFORM}>
          <HairBack look={look} />
        </g>
        {/* Cuello largo, con la sombra de la mandíbula */}
        <path d="M50,52 L50,82 L70,82 L70,52 Z" fill={c(look.skin)} stroke={INK} strokeWidth={OUTLINE} />
        <path d="M50.8,62 L69.2,62 L69.2,72.6 Q60,77 50.8,72.6 Z" fill={shade(look.skin, -0.16)} />
        <Top look={look} frame={frame} uid={uid} />
        <g transform={BODY_TRANSFORM}>
          <Neck look={look} />
        </g>
        <g transform={HEAD_TRANSFORM}>
        {/* Cabeza */}
        {[37, 83].map((x) => (
          <g key={x}>
            <ellipse cx={x} cy={58} rx={4.4} ry={6.4} fill={c(look.skin)} stroke={INK} strokeWidth={OUTLINE} />
            <path
              d={x < 60 ? 'M37.6,54 Q34.6,56 35.4,60 Q36.4,62.6 38.4,62' : 'M82.4,54 Q85.4,56 84.6,60 Q83.6,62.6 81.6,62'}
              fill="none"
              stroke={shade(look.skin, -0.3)}
              strokeWidth={1.1}
              strokeLinecap="round"
            />
          </g>
        ))}
        <clipPath id={`${uid}-face`}>
          <path d={head} />
        </clipPath>
        <path d={head} fill={c(look.skin)} />
        <g clipPath={`url(#${uid}-face)`}>
          {/* Sombra del pelo en la frente y en un lado de la cara, en plano */}
          {look.hair > 1 && look.hair !== 8 ? <path d={CAP} transform="translate(0 6)" fill={shade(look.skin, -0.1)} /> : null}
          <path d="M36,40 L44,40 Q41,62 50,80 Q54,85 60,88 L36,88 Z" fill={shade(look.skin, -0.1)} />
        </g>
        <path d={head} fill="none" stroke={INK} strokeWidth={OUTLINE} />
        <Earrings look={look} />
        <Marks look={look} />
        <Beard look={look} />
        <Nose look={look} />
        <Mouth look={look} />
        <Eyes look={look} />
        <Brows look={look} />
        <HairFront look={look} />
        {HIGHLIGHTED_HAIR.has(look.hair) ? (
          <path d="M43,33 Q58,22 80,30 Q78,32 76,31 Q60,27 46,36 Q44,35 43,33 Z" fill={shade(look.hairColor, 0.22)} />
        ) : null}
        <Glasses look={look} />
        <Hat look={look} />
        </g>
      </g>
    </svg>
  );
}
